import RootStore from '@App/stores/root-store'
import { action, makeObservable, observable, runInAction } from 'mobx'
import {
    exportCatalogTransfer,
    importCatalogTransfer,
    importCatalogTransferStream,
} from '@App/api/routes/rightsholder/catalog-transfer'
import { CatalogTransferImportResult } from '@Entities/rightsholder/catalog-transfer-import-result'
import { WorksReportRequestDTO } from '@Entities/report/works-report-request'
import { readEventStream } from '@App/helpers/streams/read-event-stream'
import { parseCatalogTransferImportStreamMsg } from '@App/helpers/catalog-transfer/import-stream-message'

export default class CatalogTransferImportStore {
    private rootStore: RootStore
    private _catalogExported = false
    private _importResult: CatalogTransferImportResult | undefined
    private _isImportStreaming = false
    private _importStreamStatus: 'idle' | 'streaming' | 'failed' | 'complete' = 'idle'
    private _importStreamProcessedCount = 0
    private _importStreamErrorCount = 0

    constructor(rootStore: RootStore) {
        makeObservable<
            CatalogTransferImportStore,
            | '_catalogExported'
            | '_importResult'
            | '_isImportStreaming'
            | '_importStreamStatus'
            | '_importStreamProcessedCount'
            | '_importStreamErrorCount'
        >(this, {
            _catalogExported: observable,
            _importResult: observable,
            _isImportStreaming: observable,
            _importStreamStatus: observable,
            _importStreamProcessedCount: observable,
            _importStreamErrorCount: observable,
            resetImport: action,
            setImportResult: action,
            startImportStream: action,
            updateImportStreamProgress: action,
            completeImportStream: action,
            failImportStream: action,
            resetImportStreamState: action,
            resetStoreAfterWizard: action,
        })

        this.rootStore = rootStore
    }

    get catalogExported() {
        return this._catalogExported
    }

    get isCatalogImported() {
        return !!this._importResult
    }

    get importResult() {
        return this._importResult
    }

    get isImportStreaming() {
        return this._isImportStreaming
    }

    get importStreamStatus() {
        return this._importStreamStatus
    }

    get importStreamProcessedCount() {
        return this._importStreamProcessedCount
    }

    get importStreamErrorCount() {
        return this._importStreamErrorCount
    }

    resetImport = () => {
        this._importResult = undefined
        this.resetImportStreamState()
    }

    setImportResult = (importResult: CatalogTransferImportResult) => {
        this._importResult = importResult
    }

    startImportStream = () => {
        this._isImportStreaming = true
        this._importStreamStatus = 'streaming'
        this._importStreamProcessedCount = 0
        this._importStreamErrorCount = 0
    }

    updateImportStreamProgress = (processed: number, errors: number) => {
        this._importStreamProcessedCount = processed
        this._importStreamErrorCount = errors
    }

    completeImportStream = () => {
        this._isImportStreaming = false
        this._importStreamStatus = 'complete'
    }

    failImportStream = () => {
        this._isImportStreaming = false
        this._importStreamStatus = 'failed'
    }

    resetImportStreamState = () => {
        this._isImportStreaming = false
        this._importStreamStatus = 'idle'
        this._importStreamProcessedCount = 0
        this._importStreamErrorCount = 0
    }

    private get currentCatalogTransfer() {
        return this.rootStore.catalogTransferWizardStore.currentCatalogTransfer
    }

    private get exportCurrentSearch() {
        return this.rootStore.catalogTransferWizardStore.exportCurrentSearch
    }

    private getRequiredTransferId() {
        const currentTransfer = this.currentCatalogTransfer
        if (!currentTransfer?.id) {
            throw new Error('No current catalog transfer to link source publisher')
        }
        return currentTransfer.id
    }

    async importWorksFromExcel() {
        const importResult = await importCatalogTransfer(
            this.rootStore.rightsholderStore.currentRightsholderId,
            this.getRequiredTransferId()
        )
        runInAction(() => {
            this._importResult = importResult.data
        })
        this.rootStore.catalogTransferListStore.invalidateCatalogTransfers()
    }

    async importWorksFromExcelStream() {
        this.resetImport()
        this.startImportStream()

        try {
            const response = await importCatalogTransferStream(
                this.rootStore.rightsholderStore.currentRightsholderId,
                this.getRequiredTransferId()
            )

            await readEventStream({
                stream: response.data,
                parse: parseCatalogTransferImportStreamMsg,
                onMessage: (msg) => {
                    if (msg.type === 'progress') {
                        this.updateImportStreamProgress(msg.processed, msg.errors)
                        return true
                    }

                    if (msg.type === 'complete') {
                        this.updateImportStreamProgress(msg.processed, msg.errors)
                        this.setImportResult(msg.response)
                        this.completeImportStream()
                        return false
                    }

                    return true
                },
            })

            const completeReceived = this._importResult !== undefined
            if (!completeReceived) {
                throw new Error('Import stream ended before completion.')
            }
            this.rootStore.catalogTransferListStore.invalidateCatalogTransfers()
        } catch {
            this.failImportStream()
            this.rootStore.popupStore.setAlertData('Catalog import stream disconnected before completion.', true)
        }
    }

    async exportWorksToExcel() {
        this.rootStore.preloaderStore.openTransparentPreloader(true)
        try {
            const format = { format: 'MEMBERCATALOGTRANSFER' }
            const workReport: WorksReportRequestDTO = this.exportCurrentSearch
                ? {
                      ...new WorksReportRequestDTO(
                          this.rootStore.transferCatalogSearchStore.catalogSelectionSearchQuery || {}
                      ),
                      ...format,
                  }
                : format

            await exportCatalogTransfer(
                this.rootStore.rightsholderStore.currentRightsholderId,
                this.getRequiredTransferId(),
                workReport
            )

            runInAction(() => {
                this._catalogExported = true
            })
            this.rootStore.catalogTransferListStore.invalidateCatalogTransfers()
        } finally {
            this.rootStore.preloaderStore.openTransparentPreloader(false)
        }
    }

    resetStoreAfterWizard = () => {
        this._catalogExported = false
        this._importResult = undefined
        this.resetImportStreamState()
    }
}
